import axios from 'axios';

// Public API client (no authentication required)
const publicApiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://hrbackend.147.93.95.174.sslip.io/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Public Job Service
 * Handles public job listings and application submission (no auth required)
 */
class PublicJobService {
  constructor() {
    this.debugMode = import.meta.env.DEV;
    this.log('PublicJobService initialized', { debugMode: this.debugMode });
  }

  /**
   * Enhanced logging with timestamps
   */
  log(message, data = {}, level = 'info') {
    const timestamp = new Date().toISOString();
    const logMessage = `[PublicJobService ${timestamp}] ${message}`;

    if (this.debugMode) {
      switch (level) {
        case 'error':
          console.error(logMessage, data);
          break;
        case 'warn':
          console.warn(logMessage, data);
          break;
        default:
          console.log(logMessage, data);
      }
    }
  }

  /**
   * Helper function to handle API responses
   */
  handleApiResponse(response) {
    if (response.data?.success) {
      return response.data.data;
    } else {
      throw new Error(response.data?.error || 'API request failed');
    }
  }

  /**
   * Helper function to handle API errors
   */
  handleApiError(error) {
    this.log('API Error', {
      message: error.message,
      status: error.response?.status,
      data: error.response?.data
    }, 'error');

    if (error.response?.data?.error) {
      throw new Error(error.response.data.error);
    } else if (error.message) {
      throw new Error(error.message);
    } else {
      throw new Error('An unexpected error occurred');
    }
  }

  /**
   * Get public job listings (active jobs only)
   * @param {object} filters - Filter parameters
   * @returns {Promise<object>} Jobs list
   */
  async getPublicJobs(filters = {}) {
    this.log('Getting public job listings', { filters });

    try {
      const params = new URLSearchParams();

      // Only get active/published jobs for public viewing
      params.append('status', 'active');

      if (filters.department) params.append('department', filters.department);
      if (filters.location) params.append('location', filters.location);
      if (filters.employment_type) params.append('employment_type', filters.employment_type);
      if (filters.experience_level) params.append('experience_level', filters.experience_level);
      if (filters.remote_work_allowed) params.append('remote_work_allowed', filters.remote_work_allowed);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page);
      if (filters.pageSize) params.append('page_size', filters.pageSize);

      // Create a public endpoint that doesn't require authentication
      // This would need to be implemented in the backend
      const response = await publicApiClient.get(`/public/jobs?${params}`);

      const data = this.handleApiResponse(response);

      this.log('Public jobs retrieved successfully', {
        count: data.jobs?.length || 0,
        total: data.total_count || 0
      });

      return data;
    } catch (error) {
      // Fallback to regular jobs endpoint if public endpoint not available
      try {
        this.log('Fallback to regular jobs endpoint', {}, 'warn');
        const response = await publicApiClient.get(`/jobs?${params}`);
        return this.handleApiResponse(response);
      } catch (fallbackError) {
        this.handleApiError(fallbackError);
      }
    }
  }

  /**
   * Get a specific job by ID (public view)
   * @param {string} jobId - Job ID
   * @returns {Promise<object>} Job details
   */
  async getPublicJob(jobId) {
    this.log('Getting public job details', { jobId });

    try {
      // Try public endpoint first
      const response = await publicApiClient.get(`/public/jobs/${jobId}`);
      const data = this.handleApiResponse(response);

      this.log('Public job details retrieved successfully', {
        jobId: data.job?.id,
        title: data.job?.title
      });

      return data;
    } catch (error) {
      // Fallback to regular endpoint
      try {
        this.log('Fallback to regular job endpoint', {}, 'warn');
        const response = await publicApiClient.get(`/jobs/${jobId}`);
        return this.handleApiResponse(response);
      } catch (fallbackError) {
        this.handleApiError(fallbackError);
      }
    }
  }

  /**
   * Submit job application (public endpoint)
   * @param {object} applicationData - Application data
   * @returns {Promise<object>} Application result
   */
  async submitApplication(applicationData) {
    this.log('Submitting job application', {
      jobId: applicationData.jobId,
      candidateName: applicationData.candidateName
    });

    try {
      const response = await publicApiClient.post('/public/job-applications/submit', {
        job_id: applicationData.jobId,
        candidate_name: applicationData.candidateName,
        candidate_email: applicationData.candidateEmail,
        candidate_phone: applicationData.candidatePhone,
        cv_text: applicationData.cvText,
        cover_letter: applicationData.coverLetter,
        auto_process_cv: applicationData.autoProcessCv || true
      });

      const data = this.handleApiResponse(response);

      this.log('Job application submitted successfully', {
        applicationId: data.application_id,
        candidateId: data.candidate_id
      });

      return data;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  /**
   * Track application status by email (public endpoint)
   * @param {string} email - Candidate email
   * @returns {Promise<Array>} Applications list
   */
  async trackApplications(email) {
    this.log('Tracking applications by email', { email });

    try {
      // This would need a special public endpoint that returns limited candidate info
      const response = await publicApiClient.get(`/public/applications/track?email=${encodeURIComponent(email)}`);
      const data = this.handleApiResponse(response);

      this.log('Applications tracking retrieved', {
        count: data.applications?.length || 0
      });

      return data;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  /**
   * Get job application statistics for a specific job (public)
   * @param {string} jobId - Job ID
   * @returns {Promise<object>} Application stats
   */
  async getJobStats(jobId) {
    this.log('Getting job application statistics', { jobId });

    try {
      const response = await publicApiClient.get(`/public/jobs/${jobId}/stats`);
      return this.handleApiResponse(response);
    } catch (error) {
      // Return empty stats if endpoint not available
      this.log('Job stats not available, returning defaults', {}, 'warn');
      return {
        total_applications: 0,
        applications_this_week: 0,
        avg_response_time: '3-5 days'
      };
    }
  }

  /**
   * Get company information (public)
   * @returns {Promise<object>} Company info
   */
  async getCompanyInfo() {
    this.log('Getting company information');

    try {
      const response = await publicApiClient.get('/public/company');
      return this.handleApiResponse(response);
    } catch (error) {
      // Return default company info if endpoint not available
      this.log('Company info not available, returning defaults', {}, 'warn');
      return {
        name: 'c-success consulting',
        description: 'Find your next opportunity with AI-powered job matching.',
        website: 'https://c-successconsulting.com',
        careers_email: 'info@c-successconsulting.com'
      };
    }
  }
}

// Create and export a singleton instance
const publicJobService = new PublicJobService();
export default publicJobService;